import React, { Fragment, useContext, useEffect, useState } from "react";
import { Button, Dropdown, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { withTranslation } from "react-i18next";
import { AiOutlineCalculator } from "react-icons/ai";
import { BiBarChartAlt2, BiCalculator, BiChevronDown, BiChevronRight, BiData, BiDetail, BiInfoCircle, BiPlus, BiPlusCircle, BiTransfer, BiTrash } from "react-icons/bi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { LoaderContext } from "../../../App";
import AssignSubscriptionService from "../../../core/service/AssignSubscriptionService";
import CompanyService from "../../../core/service/CompanyService";
import DashGraphConfigService from "../../../core/service/DashGraphConfigService";
import GHGEmissionService from "../../../core/service/GHGEmissionService";
import ProfileSettingsService from "../../../core/service/ProfileSettingsService";
import QuestionService from "../../../core/service/QuestionService";
import StandardService from "../../../core/service/StandardService";
import Utils from "../../../core/utilities/Utils";
import { EquationBuilderFor, LS_AUTHORITY_KEY } from "../../../core/constants/common";
import EquationBuilder from "../../Common/Standards/EquationBuilder/EquationBuilder";
import EquationComponentsPopover from "../../../components/EquationComponentsPopover/EquationComponentsPopover";
import SankeyDiagramConfigModal from "./SankeyDiagramConfigModal";
import "./GraphConfigForm.scss";

/** Legal name shown in Company typeahead — supports several API shapes from graph config list/detail. */
const getCompanyDisplayLabelFromEditedConfig = (cfg) => {
	if (!cfg) return '';
	const nested =
		cfg.company && typeof cfg.company === "object"
			? (cfg.company.legalName ?? cfg.company.name ?? "")
			: "";
	return (
		cfg.companyLegalName ??
		cfg.companyName ??
		cfg.legalName ??
		nested ??
		""
	);
};

/** Graph config API may return GHG fields as camelCase or snake_case. */
const getScopeFromGraphConfig = (cfg) => cfg?.scope ?? '';
const getScopeCategoryFromGraphConfig = (cfg) =>
	cfg?.scopeCategory ?? cfg?.scope_category ?? '';

/** Sankey tree from API may be JSON string or already-parsed array. */
const parseSankeyConfigForState = (raw) => {
	if (raw == null || raw === '') return [];
	if (Array.isArray(raw)) return raw;
	if (typeof raw === 'string') {
		try {
			const p = JSON.parse(raw);
			return Array.isArray(p) ? p : [];
		} catch {
			return [];
		}
	}
	return [];
};

/**
 * Embedded props (used by the company "Add Widget" edit modal):
 * graphConfig – config to edit (instead of router state); embedded – render without page header/navigation;
 * companyMode – company login: Company and Standard hidden; standardId – fixed standard for companyMode;
 * onCancel / onSaved – callbacks replacing router navigation.
 */
const GraphConfigForm = ({ t, graphConfig, embedded = false, companyMode = false, standardId: fixedStandardId = 0, onCancel, onSaved }) => {

	const navigate = useNavigate();
	const location = useLocation();
	const editedConfig = graphConfig ?? location.state?.graphConfig;
	const duplicateConfig = location.state?.duplicate === true;

	const [validated, setValidated] = useState(false);
	const [formData, setFormData] = useState({
		id: duplicateConfig ? 0 : (editedConfig?.id ?? 0),
		title: editedConfig?.title ?? '',
		description: editedConfig?.description ?? '',
		type: editedConfig?.type ?? '',
		timeInterval: editedConfig?.timeInterval ?? '',
		category: editedConfig?.category ?? '',
		xAxisLabel: editedConfig?.xAxisLabel ?? '',
		yAxisLabel: editedConfig?.yAxisLabel ?? '',
		legendPosition: editedConfig?.legendPosition ?? '',
		axisScale: editedConfig?.axisScale ?? '',
		gridLines: editedConfig?.gridLines ?? false,
		analytics: editedConfig?.analytics ?? false,
		sortOrder: editedConfig?.sortOrder ?? 1,
		standardName: editedConfig?.standardName ?? '',
		topicName: editedConfig?.topicName ?? '',
		standardId: companyMode ? fixedStandardId : 0,
		topicId: 0,
		active: companyMode ? (editedConfig?.active ?? true) : true,
		emission: editedConfig?.emission ?? false,
		showOverview: editedConfig?.showOverview ?? false,
		yearOffset: editedConfig?.yearOffset ?? '',
		scope: getScopeFromGraphConfig(editedConfig),
		scopeCategory: getScopeCategoryFromGraphConfig(editedConfig),
		isDrilldown: editedConfig?.isDrilldown ?? false
	});
	const [graphDataPoints, setGraphDataPoints] = useState(editedConfig?.dataPoints ?? []);
	const [graphProps, setGraphProps] = useState([]);
	const [standards, setStandards] = useState([]);
	const [dpIndexToSetEquation, setDPIndexToSetEquation] = useState(-1);
	const [topics, setTopics] = useState([]);
	const [uoms, setUOMs] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const initialCompanyId = companyMode
		? (JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY))?.profile?.company?.id ?? 0)
		: (editedConfig?.companyId ??
			editedConfig?.company?.id ??
			0);
	const initialCompanyLabel = getCompanyDisplayLabelFromEditedConfig(editedConfig);
	const initialCompanyOption =
		initialCompanyId > 0 && initialCompanyLabel
			? [{ id: initialCompanyId, legalName: initialCompanyLabel }]
			: [];
	const [companyId, setCompanyId] = useState(initialCompanyId);
	const [selectedCompanyItems, setSelectedCompanyItems] = useState(initialCompanyOption);
	const [companies, setCompanies] = useState(() =>
		initialCompanyOption.length > 0 ? [...initialCompanyOption] : []
	);
	const [sankeyModalOpen, setSankeyModalOpen] = useState(false);
	const [sankeyTree, setSankeyTree] = useState(() =>
		parseSankeyConfigForState(editedConfig?.sankeyTree)
	);
	const localizationObject = {
		languageCode: "en",
		title: "",
		description: ""
	};
	const localizationDPObject = {
		languageCode: "en",
		label: "",
	};
	const [localizationData, setLocalizationData] = useState([localizationObject]);
	const [dpLocalizationData, setDPLocalizationData] = useState([]);
	const [isLanguageAvailable, setIsLanguageAvailable] = useState(false);
	const [languageOptions, setLanguageOptions] = useState([]);
	const [primaryLanguageCode, setPrimaryLanguageCode] = useState('en');
	const { loaderDisplay, setLoaderDisplay } = useContext(LoaderContext);
	const [scopeData, setScopeData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);

	useEffect(() => {
		getGraphProps();
		if (companyMode) {
			if (fixedStandardId > 0) getTopics(fixedStandardId);
		} else {
			getStandards();
		}
		getUOMs();
		loadLanguages();
		setDPLocalizationData([{ localizationData: [localizationDPObject] }]);
		getScopeandCategories();
	}, []);

	/** Populate category dropdown options when scopes load (edit flow: API uses scope_category vs scopeCategory). */
	useEffect(() => {
		if (!scopeData?.length || !formData.scope) return;
		const selectedScopeData = scopeData.find((s) => s.name === formData.scope);
		if (selectedScopeData?.categories) {
			setCategoryData(selectedScopeData.categories);
		}
	}, [scopeData, formData.scope]);

	useEffect(() => {
		if (companyId <= 0 || companyMode) return;

		getCompanyStandards(companyId);
	}, [companyId]);

	/** When editing with companyId but no label on the row, resolve legal name for the Company field. */
	useEffect(() => {
		if (initialCompanyId <= 0 || initialCompanyLabel || companyMode) return;

		let cancelled = false;
		ProfileSettingsService.getCompanyDetails(initialCompanyId)
			.then((res) => {
				if (cancelled) return;
				const legalName = res.data?.legalName ?? "";
				if (!legalName) return;
				const opt = { id: initialCompanyId, legalName };
				setSelectedCompanyItems([opt]);
				setCompanies((prev) =>
					prev.some((c) => c.id === opt.id) ? prev : [...prev, opt]
				);
			})
			.catch(() => {});

		return () => {
			cancelled = true;
		};
	}, [initialCompanyId, initialCompanyLabel]);

	const loadLanguages = () => {
		CompanyService.getLanguageList().then(res => {
			let languageData = res.data.data;
			if (languageData && languageData.length > 0) {
				setIsLanguageAvailable(true);
			}

			setLanguageOptions(languageData);
			//   setPrimaryLanguageCode(primaryCode);
			if (editedConfig?.localizedContents && editedConfig.localizedContents.length > 0) {
				// Group, transform, and map the translation data
				const groupedByLanguage = groupByLanguage(editedConfig.localizedContents);
				const customArray = transformGroupedData(groupedByLanguage);
				const mappedData = mapToStateFormat(customArray);
				setLocalizationData(mappedData);
			} else {
				const localDatContent = { ...localizationObject };
				localDatContent.title = editedConfig?.title ?? '';
				localDatContent.description = editedConfig?.description ?? '';
				setLocalizationData([localDatContent]);
			}
			let dpData = editedConfig?.dataPoints ?? [];
			if (dpData.length <= 0) {
				setDPLocalizationData([{ localizationData: [localizationDPObject] }]);
			} else {
				const languages = getLanguageCodes(dpData);
				const transformedData = transformChildren(dpData, languages, primaryLanguageCode);
				// Check if any localizationData is empty
				const updatedData = transformedData.map(item => {
					if (item.localizationData.length === 0) {
						item.localizationData.push(localizationObject);
					}
					return item;
				});
				setDPLocalizationData(updatedData);

			}

		}).catch(error => {
			// setLoaderDisplay(false);
		});
	};



	const getLanguageCodes = (data) => {
		const languageCodes = new Set();

		// Loop through each item in the data array
		data.forEach(item => {
			if (Array.isArray(item.localizedContents)) {
				item.localizedContents.forEach(content => {
					if (content.languageCode) {
						languageCodes.add(content.languageCode);
					}
				});
			}
		});
		return Array.from(languageCodes);
	};

	// Transform the children array
	const transformChildren = (children, languages, primaryCode) => {
		return children.map(child => {
			if (languages.length === 0) {
				// If languages array is empty, return the values from child as localization data
				return {
					localizationData: [{
						fieldName: child.fieldName || "",
						languageCode: 'en',
						label: child.label || ""
					}]
				};
			}

			const localData = languages
				.filter(language =>
					// Check if the current language exists in the localizedContents of the child
					child.localizedContents.some(item => item.languageCode === language)
				)
				.map(language => {
					const localizedFields = {
						fieldName: "",
						languageCode: language,
						label: child.localizedContents.find(item => item.fieldName === "label" && item.languageCode === language)?.translatedValue || ""
					};
					return localizedFields;
				});

			// Check if there is no object with primary languageCode
			if (!localData.some(item => item.languageCode === primaryCode) && localData.length > 0) {
				// Set the languageCode of the first object to primaryCode
				localData[0].languageCode = primaryCode;
			}


			localData.sort((a, b) => {
				if (a.languageCode === primaryCode) return -1;
				if (b.languageCode === primaryCode) return 1;
				return 0;
			});

			return {
				localizationData: localData
			};
		});

	};



	// Function to group items by language
	const groupByLanguage = (items) => {
		return items.reduce((acc, item) => {
			const { languageCode } = item;
			if (!acc[languageCode]) acc[languageCode] = [];
			acc[languageCode].push(item);
			return acc;
		}, {});
	};


	const transformGroupedData = (groupedData) => {
		return Object.keys(groupedData).map((language) => {
			const translations = groupedData[language].reduce((acc, item) => {
				acc[item.fieldName] = item.translatedValue;
				return acc;
			}, {});

			return {
				language,
				translations,
			};
		});
	};

	const mapToStateFormat = (customArray) => {
		const result = customArray
			.map((item) => ({
				languageCode: item.language,
				title: item.translations.title,
				description: item.translations.description
			}));

		// Check if there is no object with primaryCode
		if (!result.some(item => item.languageCode === primaryLanguageCode) && result.length > 0) {
			result[0].languageCode = primaryLanguageCode;
		}

		// Sort result
		const sortedResult = result.sort((a, b) => {
			if (a.languageCode === primaryLanguageCode) return -1;
			if (b.languageCode === primaryLanguageCode) return 1;
			return 0;
		});

		return sortedResult;

	};


	const getGraphProps = () => {
		DashGraphConfigService.getProps().then(res => {
			if (res.status !== 200) return;

			setGraphProps(res.data.data ?? []);
		}).catch(err => { });
	};

	const getStandards = (setTopicsData = true) => {
		StandardService.getStandards('S', true)
			.then(res => {
				if (res.status !== 200) return;

				setStandards([...res.data]);
				if (!setTopicsData) return;

				if (formData.id === 0 && !formData.standardName) return;

				const stdId = res.data?.filter(s => s.name === formData.standardName)[0]?.id ?? 0;
				setFormData({ ...formData, standardId: stdId });
				getTopics(stdId);
			})
			.catch((error) => {
			});
	};

	const getUOMs = () => {
		StandardService.getUnitsOfMeasure()
			.then((response) => {
				if (response.status !== 200) return;

				const outputList = [];
				for (let index = 0; index < response.data.data.length; index++) {
					const element = response.data.data[index];
					outputList.push({
						id: element.id,
						label: element.outputUnit,
						value: element.outputUnit
					});
				}
				const uniqueOutputArray = Array.from(new Set(outputList.map(obj => obj.value))).map(
					value => outputList.find(obj => obj.value === value)
				);
				uniqueOutputArray.sort((a, b) => a.value.localeCompare(b.value));
				setUOMs(uniqueOutputArray);
			})
			.catch((error) => { });
	};

	const handleSave = () => {
		let req;
		setValidated(true);
		const localizedArray = [];
		if (!formData.title) return;

		if (graphDataPoints?.length === 0) {
			toast.error(t('GraphConfigDPError.label'), {
				position: toast.POSITION.TOP_RIGHT,
			});
			return;
		}
		for (let index = 0; index < graphDataPoints.length; index++) {
			const dp = graphDataPoints[index];
			if (!dp.label) return;

			if (!dp.comparison && !dp.equation) {
				toast.error(t('DPEquationMandatoryError.label'), {
					position: toast.POSITION.TOP_RIGHT,
				});
				return;
			}
		}

		const payload = companyMode
			? {
				// keep server-owned fields (adminRef, metricIds, businessUnitBased, ...) from the loaded config
				...editedConfig,
				...formData,
				dataPoints: graphDataPoints,
				sankeyTree: formData.isDrilldown ? JSON.stringify(sankeyTree) : null
			}
			: {
				...formData,
				companyId: companyId > 0 ? companyId : null,
				dataPoints: graphDataPoints,
				sankeyTree: formData.isDrilldown ? JSON.stringify(sankeyTree) : ''
			};
		if (companyMode) {
			delete payload.companyId;
			delete payload.standardId;
			delete payload.topicId;
			payload.active = editedConfig?.active === true;
		}

		localizationData.forEach((item, index) => {
			const objtitle = {
				fieldName: "title",
				languageCode: item.languageCode,
				translatedValue: item.title
			};
			const objdescription = {
				fieldName: "description",
				languageCode: item.languageCode,
				translatedValue: item.description
			};
			localizedArray.push(objtitle, objdescription);

		});

		// Transform the data and flatten the localizationData into a single array for each entry
		const transformedData = dpLocalizationData.map(item =>
			item.localizationData.flatMap(locale => [
				{
					fieldName: "label",
					languageCode: locale.languageCode,
					translatedValue: locale.label
				}
			])
		);
		payload['localizedContents'] = localizedArray;
		if (payload.dataPoints.length > 0) {
			payload.dataPoints = payload.dataPoints.map((item, index) => ({
				...item,
				localizedContents: transformedData[index] || []
			}));

			payload.dataPoints.forEach(item => {
				item.localizedContents.forEach(content => {
					if (content.languageCode === primaryLanguageCode) {
						if (content.fieldName === 'label') {
							item.question = content.translatedValue;
						}
					}
				});
			});
		}

		const localized = payload.localizedContents;
		// Find and set the translations for 'question', 'help_text', and 'sample_answer' with primary language code
		localized.forEach(content => {
			if (content.languageCode === primaryLanguageCode) {
				if (content.fieldName === 'title') {
					payload.title = content.translatedValue;
				} else if (content.fieldName === 'description') {
					payload.description = content.translatedValue;
				}
			}
		});
		if (formData.id > 0) {
			req = companyMode
				? DashGraphConfigService.updateCompanyGraph(payload)
				: DashGraphConfigService.updateConfig(payload);
		} else {
			delete payload.id;
			req = DashGraphConfigService.createConfig(payload);
		}
		setLoaderDisplay(true);
		req?.then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;

			if (embedded) onSaved?.(); else navigate('../dash_graph_config');
		}).catch(err => { setLoaderDisplay(false); });
		setValidated(true);
	};

	const addDataPoint = () => {
		const dp = {
			equationObj: [],
			equation: '',
			equationWithId: '',
			label: '',
			uom: '',
			position: '',
			color: '',
			style: '',
			yearOffset: '',
			comparison: false,
			comparisonInPercentage: false,
			verticalCalcEnabled: false
		};
		const dps = JSON.parse(JSON.stringify(graphDataPoints));
		dps.push(dp);
		dps?.forEach((d, index) => d.sortOrder = index + 1);
		// setDPLocalizationData([{ localizationData: [localizationDPObject] }]);
		if (dps.length > 1) {
			localizationDPObject.languageCode = primaryLanguageCode;
			const updatedNewDPData = [
				...dpLocalizationData,
				{ localizationData: [localizationDPObject] }
			];
			setDPLocalizationData(updatedNewDPData);
		}
		setGraphDataPoints(dps);
	};

	const updateDPFieldValue = (field, value, index) => {
		const dps = JSON.parse(JSON.stringify(graphDataPoints));
		if (!dps[index]) return;

		dps[index][field] = value;
		if (field === 'comparison') {
			dps[index]['comparisonInPercentage'] = false;
			dps[index]['comparisonStyle'] = null;
		}
		setGraphDataPoints(dps);
	};

	const onEquationUpdate = (eq) => {
		const dps = JSON.parse(JSON.stringify(graphDataPoints));
		if (!dps[dpIndexToSetEquation]) return;

		dps[dpIndexToSetEquation]['equation'] = eq.expression;
		dps[dpIndexToSetEquation]['equationWithId'] = eq.expressionWithId;
		setGraphDataPoints(dps);
		setDPIndexToSetEquation(-1);
	};

	const onDeleteDataPoint = (index) => {
		const dps = JSON.parse(JSON.stringify(graphDataPoints));
		if (index < 0) return;

		dps.splice(index, 1);
		dps?.forEach((d, index) => d.sortOrder = index + 1);
		setGraphDataPoints(dps);
	};

	const onStandardChange = (id) => {
		const name = standards?.find(s => s.id === +id)?.name ?? '';
		setFormData({ ...formData, standardName: name, standardId: +id });
		getTopics(id, companyId === 0);
	};

	const getTopics = (stdId, forSuperAdmin = false) => {
		StandardService.getTopicsByStandard(stdId, forSuperAdmin)
			.then(res => {
				if (res.status !== 200) return;

				setTopics([...res.data]);
				if (formData.id === 0 && !formData.topicName) return;

				const tId = res.data?.filter(s => s.name === formData.topicName)[0]?.id ?? 0;

				setFormData((existingData) => ({
					...existingData,
					standardId: stdId,
					topicId: tId
				}));
			})
			.catch((error) => { });
	};

	const onTopicChange = (id) => {
		const name = topics?.find(t => t.id === +id)?.name ?? '';
		setFormData(prev => ({
			...prev,
			topicName: name,
			topicId: +id
		}));
	};

	// Select fed from the graph properties of one type (DP_POSITION, DP_STYLE, ...)
	const renderDPPropSelect = (propType, field, dp, index) => (
		<select className="form-select"
			onChange={(e) => updateDPFieldValue(field, e.target.value, index)}
			value={dp[field]}>
			<option value='' key=''>{t("Select.label")}</option>
			{
				graphProps?.filter(p => p.type === propType)?.map(p => {
					return <option value={p.value} key={p.value}>{p.value}</option>
				})
			}
		</select>
	);

	const renderDPEquation = (dp, index) => (
		<div className="graph-config-dp__block">
			<div className="graph-config-dp__block-title">{t("Equation.label")}</div>
			<div className="d-flex align-items-center flex-wrap gap-2">
				<div className="AddEquation_box graph-config-equation-dropdown graph-config-dp__equation">
					<Dropdown
						show={dpIndexToSetEquation === index}
						onToggle={(nextShow) => {
							if (nextShow) setDPIndexToSetEquation(index);
							else setDPIndexToSetEquation(-1);
						}}
						autoClose={false}
					>
						<Dropdown.Toggle
							variant="ShowEquation"
							id={`graph-config-eq-${index}`}
							className="equation-dropdown-toggle"
						>
							{dp.equation ? (
								<>
									<AiOutlineCalculator className="equation-expression-icon flex-shrink-0 me-2" aria-hidden />
									{dp.equation}
								</>
							) : (
								<>
									<AiOutlineCalculator className="me-2" />
									{t("SetupGraphEquation.label")}
								</>
							)}
						</Dropdown.Toggle>
						<Dropdown.Menu className="p-0">
							{dpIndexToSetEquation === index && (
								<EquationBuilder
									stdId={formData.standardId}
									metricId={0}
									topicId={formData.topicId}
									equationObj={{ expression: dp.equation, expressionWithId: dp.equationWithId }}
									buildFor={EquationBuilderFor.GRAPH_CONFIG}
									forCompany={companyId > 0}
									discardBtnClick={() => setDPIndexToSetEquation(-1)}
									updatedEquationObj={(val) => onEquationUpdate(val)}
								/>
							)}
						</Dropdown.Menu>
					</Dropdown>
				</div>
				{dp.equation && dp.equationWithId && (
					<EquationComponentsPopover
						standardId={formData.standardId}
						equationId={dp.equationWithId}
						headerText={dp.equation}
						popoverKey={`graph-cfg-dp-${formData.id || 'new'}-${index}`}
						className="cursor text-success align-middle"
						popoverClassName="equation-components-popover--wide"
						iconSize={16}
					/>
				)}
			</div>
			<div className="d-flex align-items-center mt-3">
				<Form.Check
					type="switch"
					id={`verticalCalcCheckbox-${index}`}
					label={t("VerticalCalculationNeeded.label")}
					checked={dp.verticalCalcEnabled || false}
					onChange={() => updateDPFieldValue('verticalCalcEnabled', !dp.verticalCalcEnabled, index)}
				/>
				<OverlayTrigger
					placement="top"
					overlay={<Tooltip id={`vertical-calc-tooltip-${index}`}>{t("VerticalCalculationNeeded.tooltip.label")}</Tooltip>}
				>
					<span className="ms-2 d-inline-flex align-items-center" role="img" aria-label={t("VerticalCalculationNeeded.tooltip.label")}>
						<BiInfoCircle size={16} className="text-muted" />
					</span>
				</OverlayTrigger>
			</div>
		</div>
	);

	const renderDPLabelActions = (item, index, subIndex) => {
		const dpLocalizations = dpLocalizationData[index]?.localizationData;
		return (
			<div className="graph-config-form__locale-actions">
				{
					(subIndex > 0) && (
						<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("ClickToTranslate.label"))}>
							<button type="button" className="graph-config-form__icon-btn"
								aria-label={t("ClickToTranslate.label")}
								onClick={() => handleTranslate('sub', item.languageCode, index, subIndex)}>
								<BiTransfer />
							</button>
						</OverlayTrigger>
					)
				}
				{
					(dpLocalizations.length > 1 && (item.languageCode != primaryLanguageCode)) && (
						<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("Delete.label"))}>
							<button type="button" className="graph-config-form__icon-btn graph-config-form__icon-btn--danger"
								aria-label={t("Delete.label")}
								onClick={() => deleteLocalization(index, subIndex, 'sub')}>
								<BiTrash />
							</button>
						</OverlayTrigger>
					)
				}
				{
					((subIndex == dpLocalizations.length - 1)
						&& dpLocalizations.length < languageOptions.length) && (
						<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("AddNew.label"))}>
							<button type="button" className="graph-config-form__icon-btn graph-config-form__icon-btn--primary"
								aria-label={t("AddNew.label")}
								onClick={() => addLocalizationRow('sub', index)}>
								<BiPlusCircle />
							</button>
						</OverlayTrigger>
					)
				}
			</div>
		);
	};

	const renderDPLabels = (index) => {
		const dpLocalizations = dpLocalizationData[index]?.localizationData;
		if (!dpLocalizations?.length) return null;

		return (
			<div className="graph-config-dp__block">
				<div className="graph-config-dp__block-title">{t("Label.label")}</div>
				{dpLocalizations.map((item, subIndex) => (
					<div className="graph-config-form__locale-row" key={`graphConfigDPLocale-${index}-${subIndex}`}>
						<div className="row g-3 flex-grow-1">
							{isLanguageAvailable && <div className="col-md-5">
								{renderFieldLabel(t("Language.label"), true)}
								<select className="form-select" value={item.languageCode}
									disabled={item.languageCode == primaryLanguageCode}
									onChange={(e) => handleDPLocalizationChange("languageCode", e.target.value, index, subIndex)}>
									<option value="">Select a language</option>
									{getAvailableOptions('sub', item.languageCode, index).map(option => (
										<option value={option.code} key={option.code}>
											{option.name}
										</option>
									))}
								</select>
							</div>}
							<div className={isLanguageAvailable ? "col-md-7" : "col-12"}>
								{renderFieldLabel(t("Label.label"), true)}
								<input
									type="text"
									className="form-control"
									required
									value={item.label}
									onChange={(e) => {
										handleDPLocalizationChange("label", e.target.value, index, subIndex)
										updateDPFieldValue('label', e.target.value, index)
									}}
								/>
							</div>
						</div>
						{renderDPLabelActions(item, index, subIndex)}
					</div>
				))}
			</div>
		);
	};

	const renderDPAppearance = (dp, index) => (
		<div className="graph-config-dp__block">
			<div className="graph-config-dp__block-title">{t("Appearance.label")}</div>
			<div className="row g-3">
				<div className="col-md-6 col-xl-4">
					{renderFieldLabel(t("UnitOfMeasure.label"))}
					<select className="form-select"
						onChange={(e) => updateDPFieldValue('uom', e.target.value, index)}
						value={dp.uom}>
						<option value='' key=''>{t("Select.label")}</option>
						{
							uoms?.map(uom => {
								return <option value={uom.value} key={uom.value}>{uom.value}</option>
							})
						}
					</select>
				</div>
				<div className="col-md-6 col-xl-4">
					{renderFieldLabel(t("Position.label"))}
					{renderDPPropSelect('DP_POSITION', 'position', dp, index)}
				</div>
				<div className="col-md-6 col-xl-4">
					{renderFieldLabel(t("YearOffset.label"))}
					{renderDPPropSelect('YEAR_OFFSET', 'yearOffset', dp, index)}
				</div>
				<div className="col-md-6 col-xl-4">
					{renderFieldLabel(t("Color.label"))}
					<div className="graph-config-dp__color">
						<span className={`graph-config-dp__swatch graph-config-dp__swatch--${(dp.color || 'none').toLowerCase()}`} aria-hidden />
						<select className="form-select"
							onChange={(e) => updateDPFieldValue('color', e.target.value, index)}
							value={dp.color}>
							<option value='' key=''>{t("Select.label")}</option>
							<option value='ORANGE' key='ORANGE'>Orange</option>
							<option value='RED' key='RED'>Red</option>
							<option value='BLUE' key='BLUE'>Blue</option>
							<option value='GREEN' key='GREEN'>Green</option>
							<option value='YELLOW' key='YELLOW'>Yellow</option>
							<option value='CYAN' key='CYAN'>Cyan</option>
							<option value='VIOLET' key='VIOLET'>Violet</option>
						</select>
					</div>
				</div>
				<div className="col-md-6 col-xl-4">
					{renderFieldLabel(t("Style.label"))}
					{renderDPPropSelect('DP_STYLE', 'style', dp, index)}
				</div>
			</div>
		</div>
	);

	const renderDPComparison = (dp, index) => (
		<div className="graph-config-dp__block">
			<div className="graph-config-dp__block-title">{t("Comparison.label")}</div>
			<div className="graph-config-form__toggles">
				<Form.Check
					type="switch"
					id={`comaprisonCheck-${index}`}
					label={t("Comparison.label")}
					checked={!!dp.comparison}
					onChange={() => {
						updateDPFieldValue('comparison', !dp.comparison, index);
					}}
				/>
				{dp.comparison && (
					<Form.Check
						type="switch"
						id={`comaprisonInpercCheck-${index}`}
						label={t("ComparisonInPercentage.label")}
						checked={!!dp.comparisonInPercentage}
						onChange={() => updateDPFieldValue('comparisonInPercentage', !dp.comparisonInPercentage, index)}
					/>
				)}
			</div>
			{dp.comparison && (
				<div className="row g-3 mt-1">
					<div className="col-md-6 col-xl-4">
						{renderFieldLabel(t("ComparisonStyle.label"))}
						{renderDPPropSelect('DP_COMPARISON_STYLE', 'comparisonStyle', dp, index)}
					</div>
				</div>
			)}
		</div>
	);

	// Shown in the card header so a collapsed data point can still be identified at a glance
	const renderDPSummary = (dp) => (
		<div className="graph-config-dp__summary">
			{dp.color && (
				<span className="graph-config-dp__chip">
					<span className={`graph-config-dp__dot graph-config-dp__swatch--${dp.color.toLowerCase()}`} aria-hidden />
					{Utils.toTitleCase(dp.color)}
				</span>
			)}
			{dp.equation && (
				<span className="graph-config-dp__chip graph-config-dp__chip--equation" title={dp.equation}>
					<AiOutlineCalculator aria-hidden /> {dp.equation}
				</span>
			)}
			{dp.uom && <span className="graph-config-dp__chip">{dp.uom}</span>}
		</div>
	);

	const renderDataPoints = () => {
		return (
			graphDataPoints?.map((dp, index) => {
				const isCollapsed = collapsedDPs.includes(index);
				return (
					<div className={`graph-config-dp${isCollapsed ? ' graph-config-dp--collapsed' : ''}`}
						key={`graphConfigDP-${index + 1}`}
						id={`graphConfigDP-${index + 1}`}>
						<div className="graph-config-dp__header">
							<button type="button" className="graph-config-dp__toggle"
								aria-expanded={!isCollapsed}
								aria-label={isCollapsed ? t("Expand.label") : t("Collapse.label")}
								onClick={() => toggleDPCollapse(index)}>
								<BiChevronDown className="graph-config-dp__chevron" aria-hidden />
								<span className="graph-config-dp__number">{index + 1}</span>
								<span className="graph-config-dp__heading">
									<span className="graph-config-dp__title">{dp.label || `${t("DataPoint.label")} ${index + 1}`}</span>
									{dp.label && <span className="graph-config-dp__subtitle">{t("DataPoint.label")} {index + 1}</span>}
								</span>
							</button>
							{renderDPSummary(dp)}
							<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("Delete.label"))}>
								<button type="button" className="graph-config-form__icon-btn graph-config-form__icon-btn--danger graph-config-dp__delete"
									aria-label={t("Delete.label")}
									onClick={() => deleteDataPointCard(index)}>
									<BiTrash />
								</button>
							</OverlayTrigger>
						</div>
						{!isCollapsed && (
							<div className="graph-config-dp__body">
								{renderDPEquation(dp, index)}
								{renderDPLabels(index)}
								{renderDPAppearance(dp, index)}
								{renderDPComparison(dp, index)}
							</div>
						)}
					</div>
				);
			})
		);
	};

	const filterBy = () => true;

	const handleCompanySearch = (query) => {
		setIsLoading(true);
		AssignSubscriptionService.getCompanyList(query)
			.then(res => {
				setIsLoading(false);
				if (res.status !== 200) return;

				const options = res.data?.map((i) => ({
					id: i.companyId,
					legalName: i.legalName,
				}));
				setCompanies(options);
			})
			.catch((error) => {
			});
	};

	const getCompanyStandards = (cId) => {
		CompanyService.getCompanySubscriptions(cId, 'S')
			.then((response) => {
				if (response.status !== 200) return;

				setStandards([...response.data.regulations]);
				setFormData({ ...formData, standardName: '', standardId: 0 });
			})
			.catch((error) => { });
	};


	const handleLocalizationChange = (field, value, index) => {
		const updatedItems = [...localizationData];
		updatedItems[index] = {
			...updatedItems[index],
			[field]: value
		};
		setLocalizationData(updatedItems);
	};


	const handleDPLocalizationChange = (field, value, index, subIndex) => {
		const dataDP = dpLocalizationData;
		const updatedItems = [...dataDP[index].localizationData];
		updatedItems[subIndex] = {
			...updatedItems[subIndex],
			[field]: value
		};

		const updateddataDP = [
			...dataDP.slice(0, index),
			{
				...dataDP[index],
				localizationData: updatedItems,
			},
			...dataDP.slice(index + 1),
		];
		setDPLocalizationData(updateddataDP);
	}

	const getAvailableOptions = (type, code, index) => {
		let dataChoosed = [];
		switch (type) {
			case 'main': dataChoosed = localizationData;
				break;
			case 'sub': dataChoosed = dpLocalizationData[index].localizationData;
				break;

			default:
				break;
		}
		const selectedValues = dataChoosed.map(row => row.languageCode).filter(Boolean);

		const filtered = languageOptions.filter(
			option => {
				return !selectedValues.includes(option.code) ||
					dataChoosed.find(row => row.languageCode === code)?.languageCode === option.code
			}

		);

		return filtered;
	};

	const addLocalizationRow = (type, index) => {
		let localObject = { ...localizationObject };
		let localDPObject = { ...localizationDPObject };
		localObject.languageCode = '';
		localDPObject.languageCode = '';
		switch (type) {
			case 'main': setLocalizationData(prevItems => [...prevItems, localObject]);
				break;
			case 'sub': {
				setDPLocalizationData((prevItems) => {
					const updatedItems = [...prevItems];
					updatedItems[index] = {
						...updatedItems[index],
						localizationData: [
							...updatedItems[index].localizationData,
							localDPObject,
						],
					};
					return updatedItems;
				});

			}
				break;

			default:
				break;
		}

	};


	const checkIsMain = (type) => {
		let isMain = false;
		if (type == 'main') {
			isMain = true;
		}
		return isMain;
	}


	const handleTranslate = (type, targetLang, index, subIndex) => {
		let isMain = checkIsMain(type);
		let mainObj = {};
		const textArray = [];
		if (isMain) {
			mainObj = localizationData[0];
			textArray.push(mainObj.title);
			textArray.push(mainObj.description);
		} else {
			mainObj = dpLocalizationData[index].localizationData[0];
			textArray.push(mainObj.label);
		};
		const sourceLang = mainObj.languageCode;
		if (textArray.length > 0
			&& sourceLang != null && sourceLang != ''
			&& targetLang != null && targetLang != '') {
			QuestionService.translateText(textArray, sourceLang, targetLang).then(res => {
				if (res.status !== 200) return;
				const result = res.data.translations;
				if (isMain) {
					const updatedItems = [...localizationData];
					updatedItems[index] = {
						...updatedItems[index],
						title: result[0]?.text,
						description: result[1]?.text
					};
					setLocalizationData(updatedItems);
				} else {
					const updatedItems = [...dpLocalizationData[index].localizationData];
					updatedItems[subIndex] = {
						...updatedItems[subIndex],
						label: result[0]?.text
					};

					const updatedSubQData = [
						...dpLocalizationData.slice(0, index),
						{
							...dpLocalizationData[index],
							localizationData: updatedItems,
						},
						...dpLocalizationData.slice(index + 1),
					];
					setDPLocalizationData(updatedSubQData);

				}

			}).catch(err => { });
		} else {
			let message = '';
			if (sourceLang == '') {
				message = t("ProvideSourceMsg.label");
			} else if (targetLang == '') {
				message = t("ProvideTargetMsg.label");
			} else if (textArray.length > 0) {
				message = t("ProvideTextMsg.label");
			}
			toast.warning(message, {
				position: toast.POSITION.TOP_RIGHT,
			});
		}

	};

	const deleteLocalization = (index, subIndex, type) => {
		if (checkIsMain(type)) {
			const filtered = localizationData.filter((_, i) => i !== index);
			setLocalizationData(filtered);
		} else {
			const filteredLocalizationData = dpLocalizationData[index].localizationData.filter((_, i) => i !== subIndex);

			// Create a new array for newSubQData with the updated localizationData at qIndex
			const updatedSubData = [
				...dpLocalizationData.slice(0, index),
				{
					...dpLocalizationData[index],
					localizationData: filteredLocalizationData,
				},
				...dpLocalizationData.slice(index + 1),
			];

			setDPLocalizationData(updatedSubData);

		}

	};


  	const getScopeandCategories = () => {
    GHGEmissionService.getScopeandCategories()
      .then((response) => {
        if (response.status === 200) {
          setScopeData(response.data.data);
        }
      })
      .catch((error) => { });
  }

  const handleScopeChange = (e) => {
    const scopeValue = e.target.value;    
    const selectedScopeData = scopeData.find(scope => scope.name === scopeValue);
		setFormData({
			...formData,
			scope: scopeValue,
			scopeCategory: ''
		});
    if (selectedScopeData) {
      setCategoryData(selectedScopeData.categories);
    } else {
      setCategoryData([]);
    }
  };

	// Data point cards the user has folded away (by index)
	const [collapsedDPs, setCollapsedDPs] = useState([]);

	const toggleDPCollapse = (index) => {
		setCollapsedDPs(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]);
	};

	const toggleAllDPs = () => {
		setCollapsedDPs(prev => prev.length === graphDataPoints.length ? [] : graphDataPoints.map((_, i) => i));
	};

	// Keep the collapsed indexes aligned with the remaining cards
	const deleteDataPointCard = (index) => {
		onDeleteDataPoint(index);
		setCollapsedDPs(prev => prev.filter(i => i !== index).map(i => (i > index ? i - 1 : i)));
	};

	// Open every card so required fields inside collapsed data points show their validation state
	const onSaveClick = () => {
		setCollapsedDPs([]);
		handleSave();
	};

	// ---- Form layout: one numbered card per step ----
	const renderFormSection = (step, title, hint, icon, content, headerAction = null) => (
		<section className="graph-config-form__section">
			<div className="graph-config-form__section-header">
				<span className="graph-config-form__section-icon" aria-hidden>{icon}</span>
				<div className="graph-config-form__section-heading">
					<h6 className="graph-config-form__section-title">
						{title}
					</h6>
					{hint && <p className="graph-config-form__section-hint">{hint}</p>}
				</div>
				{headerAction && <div className="graph-config-form__section-action">{headerAction}</div>}
			</div>
			<div className="graph-config-form__section-body">{content}</div>
		</section>
	);

	const renderFieldLabel = (label, required = false, htmlFor = undefined) => (
		<label className="graph-config-form__label" htmlFor={htmlFor}>
			{label}
			{required && <span className="graph-config-form__required" aria-hidden>*</span>}
		</label>
	);

	// Select fed from the graph properties of one type (GRAPH, TIME_INTERVAL, AXIS_LABEL, ...)
	const renderGraphPropSelect = (propType, value, onChange, disabled = false) => (
		<select className="form-select" onChange={onChange} disabled={disabled} value={value}>
			<option value='' key=''>{t("Select.label")}</option>
			{
				graphProps?.filter(p => p.type === propType)?.map(p => {
					return <option value={p.value} key={p.value}>{p.value}</option>
				})
			}
		</select>
	);

	const renderLocalizationActions = (item, index) => (
		<div className="graph-config-form__locale-actions">
			{
				(index > 0) && (
					<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("ClickToTranslate.label"))}>
						<button type="button" className="graph-config-form__icon-btn"
							aria-label={t("ClickToTranslate.label")}
							onClick={() => handleTranslate('main', item.languageCode, index, null)}>
							<BiTransfer />
						</button>
					</OverlayTrigger>
				)
			}
			{
				(localizationData.length > 1 && (item.languageCode != primaryLanguageCode)) && (
					<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("Delete.label"))}>
						<button type="button" className="graph-config-form__icon-btn graph-config-form__icon-btn--danger"
							aria-label={t("Delete.label")}
							onClick={() => deleteLocalization(index, null, 'main')}>
							<BiTrash />
						</button>
					</OverlayTrigger>
				)
			}
			{
				((index == localizationData.length - 1)
					&& localizationData.length < languageOptions.length) && (
					<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("AddNew.label"))}>
						<button type="button" className="graph-config-form__icon-btn graph-config-form__icon-btn--primary"
							aria-label={t("AddNew.label")}
							onClick={() => addLocalizationRow('main', null)}>
							<BiPlusCircle />
						</button>
					</OverlayTrigger>
				)
			}
		</div>
	);

	const renderBasicDetailsSection = () => renderFormSection(1, t("BasicDetails.label"), t("NameAndDescribeChart.label"), <BiDetail />, (
		localizationData?.map((item, index) => (
			<div className="graph-config-form__locale-row" key={`graphConfigLocale-${index}`}>
				<div className="row g-3 flex-grow-1">
					{isLanguageAvailable && <div className="col-md-4">
						{renderFieldLabel(t("Language.label"), true)}
						<select className="form-select" value={item.languageCode}
							disabled={item.languageCode == primaryLanguageCode}
							onChange={(e) => handleLocalizationChange("languageCode", e.target.value, index)}>
							<option value="">Select a language</option>
							{getAvailableOptions('main', item.languageCode, null).map(option => (
								<option value={option.code} key={option.code}>
									{option.name}
								</option>
							))}
						</select>
					</div>}
					<div className={isLanguageAvailable ? "col-md-8" : "col-12"}>
						{renderFieldLabel(t("Title.label"), true)}
						<input
							type="text"
							className="form-control"
							required
							value={item?.title}
							onChange={(e) => {
								handleLocalizationChange("title", e.target.value, index)
								setFormData({ ...formData, title: e.target.value })
							}}
						/>
					</div>
					<div className="col-12">
						{renderFieldLabel(t("Description.label"))}
						<textarea
							className="form-control"
							rows={3}
							required
							value={item?.description}
							onChange={(e) => {
								handleLocalizationChange("description", e.target.value, index)
								setFormData({ ...formData, description: e.target.value })
							}}>
						</textarea>
					</div>
				</div>
				{renderLocalizationActions(item, index)}
			</div>
		))
	));

	// Emission options live inside the Data Source step
	const renderEmissionOptions = () => (
		<div className="graph-config-form__option-panel">
			<div className="graph-config-form__toggles">
				<Form.Check
					type="switch"
					id="emissionCheck"
					label={t("Emission.label")}
					checked={!!formData.emission}
					onChange={() => setFormData({ ...formData, emission: !formData.emission })}
				/>
				{formData.emission && (
					<Form.Check
						type="switch"
						id="showOverviewCheck"
						label={t("ShowOverview.label")}
						checked={!!formData.showOverview}
						onChange={() => setFormData({ ...formData, showOverview: !formData.showOverview })}
					/>
				)}
			</div>
			{formData.emission && (
				<div className="row g-3 mt-1">
					<div className="col-md-6">
						{renderFieldLabel(t("Scope.label"), true)}
						<select
							className="form-select"
							value={formData.scope}
							onChange={handleScopeChange}
							required>
							<option value="">{t("Select.label")}</option>
							{scopeData.map((scope) => (
								<option key={scope.id} value={scope.name}>
									{scope.name}
								</option>
							))}
						</select>
					</div>
					<div className="col-md-6">
						{renderFieldLabel(t("Category.label"), true)}
						<select
							className="form-select"
							value={formData.scopeCategory}
							onChange={(e) => setFormData({ ...formData, scopeCategory: e.target.value })}
							required
							disabled={!formData.scope}>
							<option value="">{t("Select.label")}</option>
							{categoryData.map((category) => (
								<option key={category.id} value={category.name}>
									{category.name}
								</option>
							))}
						</select>
					</div>
				</div>
			)}
		</div>
	);

	const renderChartSettingsSection = () => {
		const colClass = formData.timeInterval === 'YEARLY' ? 'col-md-6 col-xl-3' : 'col-md-4';
		return renderFormSection(4, t("ChartSettings.label"), t("ControlChartDisplay.label"), <BiBarChartAlt2 />, (
			<>
				<div className="graph-config-dp__block-title">{t("General.label")}</div>
				<div className="row g-3">
					<div className={colClass}>
						{renderFieldLabel(t("Type.label"))}
						{renderGraphPropSelect('GRAPH', formData.type,
							(e) => setFormData({ ...formData, type: e.target.value }))}
					</div>
					<div className={colClass}>
						{renderFieldLabel(t("TimeInterval.label"))}
						{renderGraphPropSelect('TIME_INTERVAL', formData.timeInterval, (e) => {
							setFormData({
								...formData,
								timeInterval: e.target.value,
								yearOffset: e.target.value === 'YEARLY' ? formData.yearOffset : ''
							});
						})}
					</div>
					{formData.timeInterval === 'YEARLY' && (
						<div className={colClass}>
							{renderFieldLabel(t("YearOffset.label"))}
							{renderGraphPropSelect('YEAR_OFFSET', formData.yearOffset,
								(e) => setFormData({ ...formData, yearOffset: e.target.value }))}
						</div>
					)}
					<div className={colClass}>
						{renderFieldLabel(t("Category.label"))}
						{renderGraphPropSelect('CATEGORY', formData.category,
							(e) => setFormData({ ...formData, category: e.target.value }))}
					</div>
				</div>
				<div className="graph-config-form__toggles mt-3">
					<Form.Check
						type="switch"
						id="gridlinesCheck"
						label={t("ShowGridLines.label")}
						checked={!!formData.gridLines}
						onChange={() => setFormData({ ...formData, gridLines: !formData.gridLines })}
					/>
					<Form.Check
						type="switch"
						id="analyticsCheck"
						label={t("ShowAnalytics.label")}
						checked={!!formData.analytics}
						onChange={() => setFormData({ ...formData, analytics: !formData.analytics })}
					/>
				</div>
				<div className="graph-config-form__subsection">
					<div className="graph-config-dp__block-title">{t("AxisAndLegend.label")}</div>
					{renderAxisLegendFields()}
				</div>
			</>
		));
	};

	const renderAxisLegendFields = () => (
		<div className="row g-3">
			<div className="col-md-6 col-xl-3">
				{renderFieldLabel(t("XAxisLabel.label"))}
				{renderGraphPropSelect('AXIS_LABEL', formData.xAxisLabel,
					(e) => setFormData({ ...formData, xAxisLabel: e.target.value }))}
			</div>
			<div className="col-md-6 col-xl-3">
				{renderFieldLabel(t("YAxisLabel.label"))}
				{renderGraphPropSelect('AXIS_LABEL', formData.yAxisLabel,
					(e) => setFormData({ ...formData, yAxisLabel: e.target.value }))}
			</div>
			<div className="col-md-6 col-xl-3">
				{renderFieldLabel(t("LegendPosition.label"))}
				{renderGraphPropSelect('LEGEND_POSITION', formData.legendPosition,
					(e) => setFormData({ ...formData, legendPosition: e.target.value }))}
			</div>
			<div className="col-md-6 col-xl-3">
				{renderFieldLabel(t("AxisScale.label"))}
				{renderGraphPropSelect('AXIS_SCALE', formData.axisScale,
					(e) => setFormData({ ...formData, axisScale: e.target.value }))}
			</div>
		</div>
	);

	const renderDataSourceSection = () => renderFormSection(2, t("DataSource.label"), t("ChooseDataSourceForChart.label"), <BiData />, (
		<>
			<div className="row g-3">
				{!companyMode && <div className="col-md-4">
					{renderFieldLabel(t("company.label"))}
					<AsyncTypeahead
						filterBy={filterBy}
						id="company"
						isLoading={isLoading}
						labelKey="legalName"
						minLength={2}
						selected={selectedCompanyItems}
						onSearch={handleCompanySearch}
						onChange={(selected) => {
							setSelectedCompanyItems(selected);
							if (selected[0]) {
								setCompanyId(selected[0].id);
							} else {
								setCompanyId(0);
								setFormData({ ...formData, topicName: '', topicId: 0, standardName: '', standardId: 0 });
								getStandards(false);
							}
						}}
						options={companies}
						placeholder={t("Search.label")}
						renderMenuItemChildren={(option, props) => (
							<Fragment>
								<span>{option.legalName}</span>
							</Fragment>
						)}
						clearButton={true}
					/>
				</div>}
				{!companyMode && <div className="col-md-4">
					{renderFieldLabel(t("StandardName.label"), true, "standardFilter")}
					<select id="standardFilter"
						className="form-select"
						onChange={(e) => onStandardChange(e.target.value)}
						required
						value={formData.standardId}>
						<option value='' key=''>{t("Select.label")}</option>
						{
							standards?.map(s => {
								return <option value={s.id} key={s.id}>{s.name}</option>
							})
						}
					</select>
				</div>}
				<div className="col-md-4">
					{renderFieldLabel(t("TopicName.label"), true, "topicFilter")}
					<select id="topicFilter"
						className="form-select"
						onChange={(e) => onTopicChange(e.target.value)}
						required
						value={formData.topicId}>
						<option value='' key=''>{t("Select.label")}</option>
						{
							topics?.map(t => {
								return <option value={t.id} key={t.id}>{t.name}</option>
							})
						}
					</select>
				</div>
			</div>
			<div className="graph-config-form__options">
				{renderEmissionOptions()}
				{formData.standardId > 0 && (
					<div className="graph-config-form__option-panel">
						<Form.Check
							type="switch"
							id="drilldownCheck"
							label={t("SankeyDiagram.label")}
							checked={!!formData.isDrilldown}
							onChange={() => {
								const newDrilldownValue = !formData.isDrilldown;
								setFormData({ ...formData, isDrilldown: newDrilldownValue });
							}}
						/>
						{formData.isDrilldown && (
							<div className="d-flex align-items-center flex-wrap gap-2 mt-3">
								<Button variant="outline-primary" size="sm" onClick={() => setSankeyModalOpen(true)}>
									{t("GraphConfigForm.sankey.configure.label")}
								</Button>
								{Array.isArray(sankeyTree) && sankeyTree.length > 0 && (
									<span className="graph-config-form__badge">
										{t("GraphConfigForm.sankey.nodes_count.label")}: {sankeyTree.length}
									</span>
								)}
							</div>
						)}
					</div>
				)}
			</div>
		</>
	));

	const renderDataPointsActions = () => (
		<div className="d-flex align-items-center gap-2">
			{graphDataPoints.length > 1 && (
				<Button variant="link" size="sm" className="text-decoration-none" onClick={toggleAllDPs}>
					{collapsedDPs.length === graphDataPoints.length ? t("ExpandAll.label") : t("CollapseAll.label")}
				</Button>
			)}
			<Button variant="outline-primary" size="sm" onClick={() => addDataPoint()}>
				<BiPlus aria-hidden /> {t("AddDataPoint.label")}
			</Button>
		</div>
	);

	const renderDataPointsSection = () => {
		const hasDataPoints = graphDataPoints?.length > 0;
		const title = (
			<>
				{t("DataPoints.label")}
				{hasDataPoints && <span className="graph-config-form__count">{graphDataPoints.length}</span>}
			</>
		);
		return renderFormSection(
			3,
			title,
			t("EachDataPointIsSeries.label"),
			<BiCalculator />,
			<>
				{!formData.topicId && (
					<div className="graph-config-form__notice">
						<BiInfoCircle aria-hidden /> {t("SelectStandardTopicForEquations.label")}
					</div>
				)}
				{hasDataPoints ? renderDataPoints() : (
					<button type="button" className="graph-config-form__add-tile" onClick={() => addDataPoint()}>
						<BiPlusCircle aria-hidden /> {t("AddDataPoint.label")}
					</button>
				)}
			</>,
			hasDataPoints && renderDataPointsActions()
		);
	};

	const renderFormActions = () => (
		<div className="graph-config-form__actions">
			<span className="graph-config-form__actions-note">
				<span className="graph-config-form__required" aria-hidden>*</span> {t("FieldsMarkedRequired.label")}
			</span>
			<Button variant="outline-secondary" onClick={() => (embedded ? onCancel?.() : navigate('../dash_graph_config'))}>
				{t("Cancel.label")}
			</Button>
			<Button variant="primary" onClick={onSaveClick}>
				{formData.id > 0 ? t("Update.label") : t("Create.label")}
			</Button>
		</div>
	);


	return (
		<div>
			{!embedded && <div className="List_header">
				<h5 className="MH">
					<Link
						to="../dash_graph_config"
						className="Search_iocnBox">
						{t("DashboardGraphConfig.label")}
						<BiChevronRight />
					</Link>
					{formData.id > 0 ? t("EditConfig.label") : t("NewConfig.label")}
				</h5>
			</div>}
			<Form noValidate validated={validated} className="graph-config-form">
				{renderBasicDetailsSection()}
				{renderDataSourceSection()}
				{renderDataPointsSection()}
				{renderChartSettingsSection()}
				{renderFormActions()}
			</Form>
			<SankeyDiagramConfigModal
				t={t}
				open={sankeyModalOpen}
				onClose={() => setSankeyModalOpen(false)}
				standardId={formData.standardId}
				topicId={formData.topicId}
				value={sankeyTree}
				onSave={(tree) => {
					setSankeyTree(tree);
					setSankeyModalOpen(false);
				}}
			/>
		</div>
	);
};
export default withTranslation()(GraphConfigForm);
